"use client";

import { useRef } from "react";
import { useRouter } from "next/navigation";
import { HelpCircle, Keyboard, Play, MessageCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useNavigationLoading } from "./navigation-loading-context";
import { railButtonClass, RailLabel } from "./rail-item";
import { openShortcutHelp } from "./ShortcutHelp";
import { openSupportChat } from "@/components/ui/intercom/open-support-chat";

interface HelpMenuProps {
  collapsed?: boolean;
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}

export function HelpMenu({ collapsed = false, variant = "desktop", onNavigate }: HelpMenuProps) {
  const router = useRouter();
  const { startNavigation } = useNavigationLoading();
  const skipCloseAutoFocusRef = useRef(false);
  const isMobile = variant === "mobile";

  const handleTakeTour = () => {
    startNavigation("/optimizer?restart_tour=true");
    router.push("/optimizer?restart_tour=true");
  };

  const handleKeyboardShortcuts = () => {
    skipCloseAutoFocusRef.current = true;
    onNavigate?.();
    openShortcutHelp();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={collapsed ? "Open help menu" : undefined}
          title={!isMobile && collapsed ? "Help" : undefined}
          className={railButtonClass(collapsed, isMobile)}
        >
          <HelpCircle
            className={cn("shrink-0", isMobile ? "size-[18px] text-zinc-500" : "size-4")}
            aria-hidden="true"
          />
          <RailLabel label="Help" collapsed={collapsed} isMobile={isMobile} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isMobile ? "start" : "end"}
        side={isMobile ? "top" : "right"}
        sideOffset={10}
        className="w-52"
        onCloseAutoFocus={(event) => {
          if (!skipCloseAutoFocusRef.current) return;
          event.preventDefault();
          skipCloseAutoFocusRef.current = false;
        }}
      >
        <DropdownMenuItem onSelect={() => openSupportChat()}>
          <MessageCircle className="mr-2 size-4" />
          Chat with support
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleTakeTour}>
          <Play className="mr-2 size-4" />
          Take the tour
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleKeyboardShortcuts}>
          <Keyboard className="mr-2 size-4" />
          Keyboard shortcuts
          <DropdownMenuShortcut>?</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
